import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, shareReplay, tap } from 'rxjs/operators';

/**
 * Single source of truth for data that is shared between screens.
 * Declare a slice here once (type + initial value) and read/update it anywhere
 * through AppStore instead of re-declaring it in every component.
 */
export interface AppState {
  /** Verified industries (get_industry_list). */
  industries: any[];
  /** Salutation choices from the settings response. */
  salutations: any[];
  /** Timezone choices from the settings response. */
  timezones: any[];
  /** Pending accounts-receivable proposals (sidebar badge). */
  arProposals: any[];
  /** Pre-approval leads summary (sidebar badge). */
  preapprovalLeads: any;
  /** Logged-in user's profile (0 = not loaded / logged out). */
  userProfile: any;
  /** Raw profile response used by the verification screens. */
  profileResponse: any;
  roleId: any;
  /** Current plan feature info. */
  planInfo: any;
  subscriptionBilling: any;
  /** Business default values (doctor_settings). */
  doctorDefaults: any;
  maintenance: any;
  showPersona: any;
  /** Delivered-terminal count for the session. */
  totalTerminals: number;
}

export const INITIAL_APP_STATE: AppState = {
  industries: [],
  salutations: [],
  timezones: [],
  arProposals: [],
  preapprovalLeads: {},
  userProfile: 0,
  profileResponse: 0,
  roleId: 0,
  planInfo: {},
  subscriptionBilling: {},
  doctorDefaults: 0,
  maintenance: [],
  showPersona: 0,
  totalTerminals: 0,
};

@Injectable({ providedIn: 'root' })
export class AppStore {
  private readonly state$ = new BehaviorSubject<AppState>({ ...INITIAL_APP_STATE });
  private readonly inFlight = new Map<keyof AppState, Observable<any>>();
  private readonly loaded = new Set<keyof AppState>();

  /** Stream of one slice; emits the current value on subscribe, then on every change. */
  select<K extends keyof AppState>(key: K): Observable<AppState[K]> {
    return this.state$.pipe(map((s) => s[key]), distinctUntilChanged());
  }

  /**
   * A BehaviorSubject kept in two-way sync with one slice, so code written
   * against `subject.next(x)` / `subject.value` keeps working while the data
   * lives in (and is reset with) the store.
   */
  subject<K extends keyof AppState>(key: K): BehaviorSubject<AppState[K]> {
    const subject = new BehaviorSubject<AppState[K]>(this.get(key));
    subject.subscribe((v) => v !== this.get(key) && this.set(key, v));
    this.select(key).subscribe((v) => v !== subject.value && subject.next(v));
    return subject;
  }

  /** Current value of one slice. */
  get<K extends keyof AppState>(key: K): AppState[K] {
    return this.state$.value[key];
  }

  /** Replace one slice. */
  set<K extends keyof AppState>(key: K, value: AppState[K]): void {
    this.loaded.add(key);
    this.state$.next({ ...this.state$.value, [key]: value });
  }

  /**
   * Return a slice, fetching it only if it has not been loaded yet.
   * Concurrent callers share one request; a failed request is not cached.
   * `fetcher` must throw/return `null` on failure to skip caching.
   */
  load<K extends keyof AppState>(key: K, fetcher: () => Observable<AppState[K] | null>): Observable<AppState[K]> {
    if (this.loaded.has(key)) {
      return of(this.get(key));
    }
    if (!this.inFlight.has(key)) {
      this.inFlight.set(
        key,
        fetcher().pipe(
          catchError(() => of(null)),
          tap((value) => value !== null && value !== undefined && this.set(key, value)),
          map(() => this.get(key)),
          finalize(() => this.inFlight.delete(key)),
          shareReplay(1)
        )
      );
    }
    return this.inFlight.get(key) as Observable<AppState[K]>;
  }

  /** Clear one slice (or everything) back to its initial value, e.g. on logout. */
  reset(key?: keyof AppState): void {
    if (key) {
      this.loaded.delete(key);
      this.state$.next({ ...this.state$.value, [key]: INITIAL_APP_STATE[key] });
    } else {
      this.loaded.clear();
      this.state$.next({ ...INITIAL_APP_STATE });
    }
  }
}
